import axios from "axios";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import "./App.css";
import Footer from "components/footer/Footer";
import Navbar from "components/navbar/Navbar";
import { useAppDispatch, useAppSelector } from "hooks";
import { useEffect } from "react";
import { RootState } from "store";
import domainSlice from "reducers/domainSlice";
import uiSlice from "reducers/uiSlice";
import {
  resellersSelector,
  resellersFailedSelector,
} from "selectors/domainSelectors";
import { isPrerendering } from "utility/urlUtility";
import {
  getLanguageFromPath,
  localizePath,
  stripLangPrefix,
} from "utility/languageUtility";
import i18n from "./i18n";

// Kendo React license is activated at build time via `npx kendo-ui-license activate`
// See telerik-license.txt in the root folder

axios.defaults.baseURL = process.env["REACT_APP_API_BASE_URL"];

const SetUpAxiosInterceptors = () => {
  axios.interceptors.response.use(
    function (response: any) {
      return response;
    },
    function (error: any) {
      if (error.response && error.response.status === 400) {
        let errorMessages = [];
        for (const property in error.response.data.errors) {
          errorMessages.push(error.response.data.errors[property][0]);
        }
        if (errorMessages.length > 0)
          error.message = errorMessages.join("\r\n");
        else if (error.response.data.detail)
          error.message = error.response.data.detail;
      }
      return Promise.reject(error);
    },
  );
};

function App() {
  SetUpAxiosInterceptors();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const resellers = useAppSelector((state: RootState) =>
    resellersSelector(state),
  );

  const resellersFailed = useAppSelector((state: RootState) =>
    resellersFailedSelector(state),
  );

  // Sync URL language to Redux state and i18n.
  // Domain pages (/domain/:domain) have no language prefix — honour the stored
  // preference so navigating to a domain page doesn't clobber the user's choice.
  useEffect(() => {
    const isDomainPage = location.pathname.startsWith("/domain/");
    if (isDomainPage) {
      try {
        const savedLang =
          (localStorage.getItem("language") as "en" | "es" | "zh-CN") ?? "en";
        dispatch(uiSlice.actions.setLanguageSilent(savedLang));
        i18n.changeLanguage(savedLang);
      } catch {
        dispatch(uiSlice.actions.setLanguageSilent("en"));
        i18n.changeLanguage("en");
      }
      return;
    }
    const urlLang = getLanguageFromPath(location.pathname);
    dispatch(uiSlice.actions.setLanguage(urlLang));
    i18n.changeLanguage(urlLang);
  }, [location.pathname, dispatch]);

  // If user explicitly saved a language preference but landed on a non-matching URL,
  // redirect them — except for domain pages (no es equivalent) and during pre-rendering.
  useEffect(() => {
    if (isPrerendering()) return;
    const urlLang = getLanguageFromPath(location.pathname);
    const isDomainPage = location.pathname.startsWith("/domain/");
    if (isDomainPage) return;
    try {
      const savedLang = localStorage.getItem("language") as
        | "en"
        | "es"
        | "zh-CN"
        | null;
      if (savedLang && savedLang !== urlLang) {
        const basePath = stripLangPrefix(location.pathname);
        const target = localizePath(basePath, savedLang);
        navigate(target, { replace: true });
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Skip API calls during pre-rendering
    if (isPrerendering()) {
      return;
    }

    if (!resellers && !resellersFailed) {
      dispatch(domainSlice.actions.getResellers());
    }
  }, [resellers, resellersFailed, dispatch]);

  return (
    <div className="App">
      <Navbar />
      <Outlet />
      <Footer />
    </div>
  );
}
export default App;
